//@flow
import { ListSubheader, Switch, TextField } from "@mui/material";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers-pro";
import React from 'react';
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { relativeDate } from "../../../../../helpers/date";
import { normalizeGreek } from "../../../../../helpers/normalizeGreek";
import { Update } from "../../../../../hooks/exhibitions/useExhibitionAction";
import { useDebouncedCallback } from "../../../../../hooks/useDebouncedCallback";
import useUserData from "../../../../../hooks/user/useUserData";
import { EXHIBITION } from "../../../../../locales/components/namespaces";
import type { Exhibition } from "../../../../../types/exhibition/types";
import CatalogueLanguage from "./components/catalogueLanguage";
import DisplayBrandsInCatalog from "./components/displayBrandsInCatalog";
import HideCatalogueAddress from "./components/hideCatalogueAddress";
import HideCatalogueProfile from "./components/hideCatalogueProfile";

type Props = {
	id: string,
	exhibition: Exhibition,
	updateExhibition: Function
}

const CatalogSettings = (props: Props) => {
	const { t } = useTranslation(EXHIBITION)
	const { dateFormat } = useUserData()
	const { id, exhibition, updateExhibition } = props
	const daysForCatalogSubmissionInDate = exhibition.daysForCatalogSubmission ? relativeDate({
		startDate: exhibition.dateFrom,
		days: exhibition.daysForCatalogSubmission
	}) : null
	const daysForCatalogNotification = typeof exhibition.daysForCatalogNotification === 'number' && typeof exhibition.daysForCatalogSubmission === 'number' ? exhibition.daysForCatalogNotification + exhibition.daysForCatalogSubmission : typeof exhibition.daysForCatalogNotification === 'number' ? exhibition.daysForCatalogNotification : null
	
	const daysForCatalogNotificationInDate = daysForCatalogNotification ? relativeDate({
		startDate: exhibition.dateFrom,
		days: daysForCatalogNotification
	}) : null
	
	const onChange = (value) => {
		if (!value) {
			updateExhibition(id, null, Update.DaysForCatalogSubmission)
		} else if (value.isValid()) {
			const daysForCatalogSubmission = relativeDate({ startDate: exhibition.dateFrom, actionDate: value })
			updateExhibition(id, daysForCatalogSubmission, Update.DaysForCatalogSubmission)
		}
	}
	const onChangeDaysForCatalogNotification = (value) => {
		if (!value) {
			updateExhibition(id, null, Update.DaysForCatalogNotification)
		} else if (value.isValid()) {
			const daysToAlert = relativeDate({ startDate: exhibition.dateFrom, actionDate: value })
			const daysForCatalogNotification = typeof daysToAlert === 'number' && typeof exhibition.daysForCatalogSubmission === 'number' ? daysToAlert - exhibition.daysForCatalogSubmission : null
			updateExhibition(id, daysForCatalogNotification, Update.DaysForCatalogNotification)
		}
	}
	const onChangeCatalogueProfileLength = (value) => {
		updateExhibition(id, value, Update.CatalogueProfileLength)
	}
	
	const {
		value: catalogueProfileLength,
		debouncedOnChange: debouncedOnChangeCatalogueLength
	} = useDebouncedCallback(onChangeCatalogueProfileLength, exhibition.catalogueProfileLength)
	const togglePreregistrationEnabled = () => {
		updateExhibition(id, !exhibition.catalogLocked, Update.CatalogLocked)
	}
	const toggleCatalogKioskSign = () => {
		updateExhibition(id, !exhibition.catalogEnableKioskSign, Update.CatalogEnableKioskSign)
	}
	const toggleIsKioskSignEditable = () => {
		updateExhibition(id, !exhibition.isKioskSignEditable, Update.isKioskSignEditable)
	}
	return (
		<List
			subheader={
				<ListSubheader sx={{ backgroundColor: '#F9FBF2' }}>
					<Typography
						variant={'button'}
						sx={{ color: '#000000', textTransform: 'uppercase' }}>
						{normalizeGreek(t('Catalogue'))}
					</Typography>
				</ListSubheader>
			}
			sx={{ mb: 1, pb: 0, width: '100%', bgcolor: 'background.paper', border: '1px solid #8c9eff' }}
		>
			<CatalogueLanguage
				id={id}
				exhibition={exhibition}
				updateExhibition={updateExhibition}
			/>
			<Divider component="li"/>
			<ListItem>
				<ListItemText
					primary={t('Catalogue Locked')}
					secondary={t('If you enable this, then exhibitors will not be able to edit their catalogue information. However they will be able to view them. Organizers will not be able to edit exhibitor catalogues either. Catalogues can only be updated from EnterSoft data transfer.')}
				/>
				<Switch
					edge="end"
					onChange={togglePreregistrationEnabled}
					checked={exhibition.catalogLocked}
				/>
			</ListItem>
			<Divider component="li"/>
			<ListItem
				disabled={exhibition.catalogLocked}
			>
				<ListItemText
					primary={t("Date where catalog becomes non editable")}
					secondary={t("Exhibitors will not be able to update their catalog profile after the date specify here. Organizers will still be able to update exhibitor catalogues after this date.")}
				/>
				<DatePicker
					value={daysForCatalogSubmissionInDate}
					format={dateFormat}
					onChange={(newValue) => onChange(newValue)}
					clearable
					slots={{ textField: TextField }}
					slotProps={{
						textField: { size: 'small', variant: 'filled' },
						field: { clearable: true }, // this enables the "X" clear icon
					}}
					sx={{ minWidth: '250px' }}
				/>
			</ListItem>
			<Divider component="li"/>
			<ListItem>
				<ListItemText
					primary={t("Date before end of catalogue submission to alert exhibitors")}
					secondary={t("If email type 'Ενημέρωση Καταλόγου' is activated, set here the date before end of catalogue submission, that an automatic e-mail will alert exhibitors.")}
				/>
				<DatePicker
					value={daysForCatalogNotificationInDate}
					format={dateFormat}
					onChange={(newValue) => onChangeDaysForCatalogNotification(newValue)}
					clearable
					slots={{ textField: TextField }}
					slotProps={{
						textField: { size: 'small', variant: 'filled' },
						field: { clearable: true }, // this enables the "X" clear icon
					}}
					sx={{ minWidth: '250px' }}
				/>
			</ListItem>
			<Divider component="li"/>
			<ListItem>
				<ListItemText
					primary={t("Default maximum number of characters in catalog")}
				/>
				<NumericFormat
					defaultValue={catalogueProfileLength}
					onChange={debouncedOnChangeCatalogueLength}
					variant={'filled'}
					slotProps={{
						htmlInput: {
							maxLength: 5
						}
					}}
					required
					thousandSeparator={''}
					decimalScale={0}
					allowNegative={false}
					customInput={TextField}
					size={'small'}
					allowLeadingZeros={true}
				/>
			</ListItem>
			<Divider component="li"/>
			<ListItem>
				<ListItemText
					primary={t('Show kiosk sign')}
				/>
				<Switch
					edge="end"
					onChange={toggleCatalogKioskSign}
					checked={exhibition.catalogEnableKioskSign}
				/>
			</ListItem>
			<Divider component="li"/>
			<ListItem>
				<ListItemText
					primary={t('Kiosk Sign Editable')}
					secondary={t('Allow exhibitor to edit their kiosk sign. You can disable this feature for specific exhibitors, from exhibitor settings.')}
				/>
				<Switch
					edge="end"
					onChange={toggleIsKioskSignEditable}
					checked={exhibition.isKioskSignEditable}
				/>
			</ListItem>
			<Divider component="li"/>
			<DisplayBrandsInCatalog
				id={id}
				exhibition={exhibition}
				updateExhibition={updateExhibition}
			/>
			<Divider component="li"/>
			<HideCatalogueAddress
				id={id}
				exhibition={exhibition}
				updateExhibition={updateExhibition}
			/>
			<Divider component="li"/>
			<HideCatalogueProfile
				id={id}
				exhibition={exhibition}
				updateExhibition={updateExhibition}
			/>
		</List>
	)
}

export default CatalogSettings;